// Boot breadcrumbs that survive a wedged renderer.
//
// ── The problem this exists for ──────────────────────────────────────────
// When the main thread locks, EVERY read path dies with it: CDP evaluate
// times out, screenshots time out, console reads return nothing, and a
// post-hoc probe cannot run because there is no post. Measured on
// app.bellforge.eu after a persona sign-in — a bare `1+1` failed to return
// inside 45s across three reproductions. So the trail has to be written
// BEFORE the wedge and read AFTER a reload, which means synchronously, to
// sessionStorage, from inside the boot path itself.
//
// ── The two signatures it separates ──────────────────────────────────────
// This is the whole point; a plain log would not do it.
//   • A LOOP shows as one label with a huge `n`. Something re-entered.
//   • A LONG SYNCHRONOUS BLOCK shows as a trail that simply STOPS at one
//     label with `n: 1` and never reaches the next. Something is slow, not
//     circular. (smbls element preparation is super-linear in component
//     size, and a big enough tree locks for tens of seconds while looking
//     exactly like an infinite loop.)
// Guessing between those two costs hours. The trail answers it in one read.
//
// ── Why it cannot blow up under the very loop it is diagnosing ───────────
// A naive `setItem(JSON.stringify(...))` per call would add a serialize +
// storage write to every iteration of a hot loop and become a second
// performance bug on top of the one under investigation. So the counts live
// in memory and only a THINNING schedule of occurrences is flushed
// (1,2,3, then powers of two). The last flush therefore always reflects a loop in
// progress, at a cost that falls away as the loop grows.
//
// OFF unless asked for: `?trail=1` arms it for the session (surviving the
// full-page reload the persona sign-in does), `?trail=0` disarms.

const KEY = 'bootTrail'
const ARMED = 'bootTrailArmed'

// In-memory; sessionStorage carries only the flushed snapshot.
const _counts = new Map()
const _order = []

// FA201 / frank trap (WS-SHARED-MODULE-LET-BLOCKS-EVERY-PUSH-1) — the armed
// memo used to be a module-scope `let _armed = null`, read and written by
// `bootTrailArmed` below. frank hoists that export into the emitted
// `globalScope` and revives it with a bare indirect eval, where nothing
// module-level is in scope, so the read emitted a guaranteed ReferenceError
// and frank's emission gate refused the whole publish
// (`PUBLISH BLOCKED — [unresolvable-identifier]`) for EVERY project that
// inlines this package.
//
// Re-homed onto `globalThis.__bootTrailState`, per the pointerCapability.js
// template. `globalThis` rather than `window` because `bootTrailArmed` has a
// real no-window path (it accepts `win` as an argument and still reads
// sessionStorage), and `globalThis` is defined in every runtime this package
// loads in; in a browser it IS `window`. The lazy create is INLINED at the
// one read site on purpose — a shared module-scope getter would just relocate
// the same bare-sibling-call hazard this fix removes.
//
// Tri-state, unchanged: `null` = not yet resolved, `true`/`false` = resolved.

const _session = () => {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null
  } catch (_) {
    return null
  }
}

/**
 * Is the trail armed? `?trail=1` arms it and is remembered for the tab, so
 * a full-page reload mid-flow does not silently disarm the instrument.
 */
export const bootTrailArmed = (win = typeof window !== 'undefined' ? window : null) => {
  if (!globalThis.__bootTrailState) globalThis.__bootTrailState = { armed: null }
  const state = globalThis.__bootTrailState
  if (state.armed !== null) return state.armed
  const ss = _session()
  try {
    const q = win && win.location ? String(win.location.search || '') : ''
    if (/[?&]trail=1\b/.test(q)) {
      ss?.setItem?.(ARMED, '1')
      state.armed = true
      return state.armed
    }
    if (/[?&]trail=0\b/.test(q)) {
      ss?.removeItem?.(ARMED)
      state.armed = false
      return state.armed
    }
    state.armed = ss?.getItem?.(ARMED) === '1'
  } catch (_) {
    state.armed = false
  }
  return state.armed
}

// Flush on powers of two (plus the first few), so the write cost of a hot
// loop falls away as it grows while the stored trail NEVER goes silent —
// every doubling window contains exactly one flush, so the last flush is
// always within 2x of the true count.
//
// An earlier version used an ad-hoc 5/10/25/50… ladder. Its own unit test
// caught that it stopped flushing entirely above a certain n, which would
// have made a runaway loop read as a "stopped here" block — the exact
// misdiagnosis this module exists to prevent. Keep this trivially correct.
export const shouldFlushBootTrail = (n) => n <= 3 || (n & (n - 1)) === 0

/**
 * Drop one breadcrumb. Cheap, synchronous, never throws — a diagnostic that
 * can break the boot it is diagnosing is worse than no diagnostic.
 *
 * @param {string} label  stable identifier, e.g. 'workspaceConfigs:apply'
 * @param {*} [detail]    small scalar for context; objects are not walked
 */
export const bootTrail = (label, detail) => {
  try {
    if (!bootTrailArmed()) return
    const key = String(label || '?')
    const prev = _counts.get(key)
    const n = (prev ? prev.n : 0) + 1
    const at = typeof performance !== 'undefined' ? Math.round(performance.now()) : 0
    if (!prev) _order.push(key)
    _counts.set(key, {
      n,
      firstAt: prev ? prev.firstAt : at,
      lastAt: at,
      // Last detail wins: in a loop the newest iteration is the informative
      // one, and keeping every value would reintroduce unbounded growth.
      detail: detail === undefined ? (prev ? prev.detail : undefined) : detail
    })
    if (!shouldFlushBootTrail(n)) return
    const ss = _session()
    if (!ss) return
    ss.setItem(KEY, JSON.stringify(_order.map((k) => ({ label: k, ..._counts.get(k) }))))
  } catch (_) {}
}

/** Read the trail back after a reload. Returns [] when nothing was recorded. */
export const readBootTrail = () => {
  try {
    const raw = _session()?.getItem?.(KEY)
    const parsed = raw ? JSON.parse(raw) : null
    return Array.isArray(parsed) ? parsed : []
  } catch (_) {
    return []
  }
}

/** Clear it, so the next run is not read as a continuation of this one. */
export const clearBootTrail = () => {
  try {
    _counts.clear()
    _order.length = 0
    _session()?.removeItem?.(KEY)
  } catch (_) {}
}

/**
 * One-line verdict for an operator, so nobody has to eyeball raw counts.
 * Deliberately states what the evidence supports and no more: a repeated
 * label is a loop, a trail that stops is a block, and "inconclusive" is a
 * real answer rather than a guess dressed up as one.
 */
export const summarizeBootTrail = (trail = readBootTrail(), loopThreshold = 25) => {
  if (!trail.length) return 'no breadcrumbs — the trail was not armed, or the boot never started'
  const worst = trail.reduce((a, b) => (b.n > a.n ? b : a), trail[0])
  const last = trail[trail.length - 1]
  if (worst.n >= loopThreshold) {
    return `LOOP: "${worst.label}" ran ${worst.n}x (first ${worst.firstAt}ms, last ${worst.lastAt}ms) — re-entered, not slow`
  }
  return `STOPPED AT: "${last.label}" (n=${last.n}, ${last.lastAt}ms) — reached it and never left; suspect one long synchronous block, not a loop`
}
